import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom gold pin (uses theme colour via currentColor — set in CSS).
function buildIcon(active) {
	return L.divIcon({
		className: 'poi-marker' + (active ? ' poi-marker--active' : ''),
		html: '<span class="poi-marker__dot"></span>',
		iconSize: [22, 22],
		iconAnchor: [11, 11],
	});
}

function initSection(section) {
	const mapEl = section.querySelector('.poi-map');
	if (!mapEl) return;

	const items = Array.from(section.querySelectorAll('.poi-item[data-lat][data-lng]'));
	if (!items.length) return;

	const centerLat = parseFloat(mapEl.dataset.mapCenterLat);
	const centerLng = parseFloat(mapEl.dataset.mapCenterLng);
	const zoom = parseInt(mapEl.dataset.mapZoom, 10) || 15;

	const map = L.map(mapEl, {
		center: [centerLat, centerLng],
		zoom,
		scrollWheelZoom: false,
		zoomControl: true,
	});

	L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		attribution: '&copy; OpenStreetMap',
		maxZoom: 19,
	}).addTo(map);

	const markers = new Map(); // poiId -> { marker, item, tabIndex }

	items.forEach((item) => {
		const lat = parseFloat(item.dataset.lat);
		const lng = parseFloat(item.dataset.lng);
		const poiId = item.dataset.poiId;
		const list = item.closest('[data-tab-content]');
		const tabIndex = list ? list.dataset.tabContent : '0';
		const name = item.querySelector('h4')?.textContent || '';
		const addr = item.querySelector('p')?.textContent || '';

		const marker = L.marker([lat, lng], { icon: buildIcon(false) });
		marker.bindPopup(
			'<strong>' + escapeHtml(name) + '</strong>' +
			(addr ? '<br><span>' + escapeHtml(addr) + '</span>' : ''),
			{ closeButton: false, offset: [0, -6] }
		);

		marker.on('click', () => activate(poiId, { panTo: false }));

		markers.set(poiId, { marker, item, tabIndex });
	});

	function showTab(tabIndex) {
		const bounds = [];
		markers.forEach(({ marker, tabIndex: tIdx }) => {
			if (tIdx === tabIndex) {
				marker.addTo(map);
				bounds.push(marker.getLatLng());
			} else {
				map.removeLayer(marker);
			}
		});
		if (bounds.length > 1) {
			map.fitBounds(L.latLngBounds(bounds), { padding: [32, 32], maxZoom: 16 });
		} else if (bounds.length === 1) {
			map.setView(bounds[0], zoom);
		}
		clearActive();
	}

	function activate(poiId, { panTo = true } = {}) {
		clearActive();
		const entry = markers.get(poiId);
		if (!entry) return;
		entry.item.classList.add('is-active');
		entry.marker.setIcon(buildIcon(true));
		entry.marker.openPopup();
		if (panTo) map.panTo(entry.marker.getLatLng());
	}

	function clearActive() {
		markers.forEach(({ marker, item }) => {
			item.classList.remove('is-active');
			marker.setIcon(buildIcon(false));
		});
	}

	// POI item → marker
	items.forEach((item) => {
		item.addEventListener('click', () => activate(item.dataset.poiId));
	});

	// Tabs
	section.querySelectorAll('.poi-tabs button').forEach((btn) => {
		btn.addEventListener('click', () => {
			section.querySelectorAll('.poi-tabs button').forEach((b) => b.classList.remove('active'));
			btn.classList.add('active');
			const idx = btn.dataset.tab;
			section.querySelectorAll('[data-tab-content]').forEach((list) => {
				list.style.display = list.dataset.tabContent === idx ? '' : 'none';
			});
			showTab(idx);
		});
	});

	// Leaflet needs a layout pass once the container has its final size.
	requestAnimationFrame(() => {
		map.invalidateSize();
		const activeBtn = section.querySelector('.poi-tabs button.active');
		showTab(activeBtn ? activeBtn.dataset.tab : '0');
	});
}

function escapeHtml(s) {
	return String(s).replace(/[&<>"']/g, (c) => ({
		'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
	}[c]));
}

export function initPoiMaps() {
	document.querySelectorAll('.poi-section').forEach(initSection);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPoiMaps);
} else {
	initPoiMaps();
}
